import React from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import debounce from 'lodash/debounce';
import sortBy from 'lodash/sortBy';
import values from 'lodash/values';
import { connect } from 'react-redux';
import { Motion, StaggeredMotion } from 'react-motion';
import { EMPTY_ARRAY, FULL_STYLE, RESPONSIVE_SIZE } from '../../constants';
import { setRuntimeVariable } from '../../actions/runtime';
import { springify } from '../../lib/Motion';
import ModalConductor from '../../containers/ModalConductor';
import ElementTransition from '../../components/ElementTransition';
import Header from '../../components/Header';
import { Brandmark } from '../../components/Icons';
import PerfWidget from '../../components/PerfWidget';
import Style from '../../components/Style';

// external-global styles must be imported in your JS.
import normalizeCss from './../reset-normalize.css';

const modalAnimatedDefault = { opacity: 0 };
const headerAnimatedDefault = { show: 0 };
const loadingAnimatedDefault = [{ x: 1 }, { x: 1 }, { x: 0 }];
const triggerPoints = [0, 1, 0.9];

class Layout extends React.PureComponent {
  static propTypes = {
    children: PropTypes.node,
    responsive: PropTypes.number,
    transitionId: PropTypes.string,
    showModal: PropTypes.bool,
    scrollPrison: PropTypes.bool,
    layoutLoading: PropTypes.bool,
    layoutLoaded: PropTypes.bool,
    showHeader: PropTypes.bool,
    headerNavLinks: PropTypes.array,
    setLayoutLoading: PropTypes.func,
    setLayoutLoaded: PropTypes.func,
    setResponsiveSize: PropTypes.func,
  };
  static defaultProps = {
    showHeader: false,
    headerNavLinks: EMPTY_ARRAY,
    transitionId: '__transitionId__',
  };
  static contextTypes = {
    store: PropTypes.object,
  };

  constructor(props) {
    super(props);

    this.state = {
      scrollX: 0,
      scrollY: 0,
      scrollPrison: false,
      renderModal: props.showModal,
      renderHeader: props.showHeader,
      showLogo: false,
    };
  }

  componentDidMount() {
    window.addEventListener('resize', debounce(this.onResize, 100), {
      passive: true,
    });
    this.onResize();

    setTimeout(() => this.setState({ showLogo: true }), 400);
    setTimeout(this.props.setLayoutLoading, 1000, false);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', debounce(this.onResize, 100), {
      passive: true,
    });
  }

  componentWillReceiveProps(nextProps) {
    if (!this.props.showModal && nextProps.showModal) {
      this.setState({
        scrollX: window.pageXOffset,
        scrollY: window.pageYOffset,
        renderModal: true,
      });
    } else if (this.props.showModal && !nextProps.showModal) {
      this.setState({ scrollPrison: false });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.scrollPrison && !this.state.scrollPrison) {
      window.scrollTo(this.state.scrollX, this.state.scrollY);
    }
  }

  onResize = () => {
    const width = window.innerWidth;
    const size = sortBy(values(RESPONSIVE_SIZE)).reduce(
      (acc, next) => (width >= next ? next : acc),
    );
    this.props.setResponsiveSize(size);
  };

  modalTransitionDone = () => {
    if (this.props.showModal) {
      this.setState({ scrollPrison: true });
    } else {
      this.setState({ renderModal: false });
    }
  };

  loadingAnimationDone = () => {
    if (this.props.layoutLoading) {
      return;
    }
    this.props.setLayoutLoaded(true);
  };

  getStyles = prevInterpolatedStyles => {
    if (prevInterpolatedStyles[2].x > 0.95) {
      setTimeout(this.loadingAnimationDone, 100);
    }

    let progress = 1;
    if (this.props.layoutLoading) {
      progress = 0.55;
    }

    return [
      springify({
        x: this.props.layoutLoading || !this.props.layoutLoaded ? 1 : 0,
      }),
      springify({
        x: this.state.showLogo ? 1 : 1,
      }),
      springify(
        {
          x: prevInterpolatedStyles[1].x > triggerPoints[2] ? progress : 0,
        },
        { stiffness: 80, damping: 25 },
      ),
    ];
  };

  setShowHeader = () => {
    this.setState({ renderHeader: this.props.showHeader });
  };

  setHideHeader = () => {
    if (!this.props.showHeader) {
      this.setState({ renderHeader: this.props.showHeader });
    }
  };

  render() {
    const {
      children,
      responsive,
      transitionId,
      showModal,
      scrollPrison,
      layoutLoading,
      layoutLoaded,
      showHeader,
      headerNavLinks,
      setLayoutLoading,
      setLayoutLoaded,
      setResponsiveSize,
      ...otherProps
    } = this.props;

    let bodyStyle = { opacity: layoutLoading ? 0 : 1 };
    if (this.state.scrollPrison || scrollPrison) {
      bodyStyle = {
        maxHeight: '100vh',
        position: 'relative',
        top: `-${this.state.scrollY}px`,
      };
    }

    let headerTop = 0;
    if (responsive >= RESPONSIVE_SIZE.TABLET_LANDSCAPE) {
      headerTop = 2;
    }

    const modalAnimatedCurrent = springify({ opacity: showModal ? 1 : 0 });
    const headerAnimatedCurrent = springify({
      show: this.props.showHeader ? 1 : 0,
    });

    return (
      <div style={{ overflow: 'hidden' }}>
        {this.state.renderModal && (
          <Motion
            defaultStyle={modalAnimatedDefault}
            style={modalAnimatedCurrent}
            onRest={this.modalTransitionDone}
          >
            {tween => {
              return (
                <div
                  style={{
                    ...tween,
                    willChange: 'opacity',
                    pointerEvents: tween.opacity < 0.75 ? 'none' : undefined,
                    position: 'relative',
                    zIndex: 2000,
                  }}
                >
                  <ModalConductor />
                </div>
              );
            }}
          </Motion>
        )}

        <div style={bodyStyle}>
          <Motion
            defaultStyle={headerAnimatedDefault}
            style={headerAnimatedCurrent}
          >
            {tween => {
              if (tween.show > 0) {
                return (
                  <div
                    style={{
                      position: 'absolute',
                      top: `${headerTop}rem`,
                      left: 0,
                      right: 0,
                      zIndex: 1001,
                      opacity: tween.show,
                    }}
                  >
                    <Header navLinks={headerNavLinks} />
                  </div>
                );
              } else {
                return null;
              }
            }}
          </Motion>
          {children}
        </div>
        {!layoutLoaded && (
          <StaggeredMotion
            defaultStyles={loadingAnimatedDefault}
            styles={this.getStyles}
          >
            {tweens => {
              return (
                <div
                  style={{
                    ...FULL_STYLE,
                    zIndex: 9999,
                    opacity: tweens[0].x,
                    pointerEvents: tweens[0].x < 0.75 ? 'none' : undefined,
                    position: 'fixed',
                    backgroundColor: Style.Colors.white,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <div
                    style={{
                      position: 'relative',
                    }}
                  >
                    <div
                      style={{
                        opacity: tweens[1].x,
                        transform: `scale(${(tweens[1].x * -1 + 1) * 0.3 + 1})`,
                      }}
                    >
                      <Brandmark />
                    </div>
                    <div
                      style={{
                        ...FULL_STYLE,
                        left: `${tweens[2].x * 100}%`,
                        opacity: 0.85,
                        transform: `scale(${(tweens[1].x * -1 + 1) * 0.3 + 1})`,
                        backgroundColor: Style.Colors.white,
                      }}
                    />
                  </div>
                </div>
              );
            }}
          </StaggeredMotion>
        )}
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    responsive: state.modal.responsive,
    showModal: state.modal.visible,
    scrollPrison: state.runtime.scrollPrison,
    layoutLoading: state.runtime.layoutLoading,
    layoutLoaded: state.runtime.layoutLoaded,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    setLayoutLoaded: value =>
      dispatch(setRuntimeVariable('layoutLoaded', value)),
    setLayoutLoading: value =>
      dispatch(setRuntimeVariable('layoutLoading', value)),
    setResponsiveSize: size => dispatch(setRuntimeVariable('responsive', size)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(
  withStyles(normalizeCss)(Layout),
);



// WEBPACK FOOTER //
// ./src/components/Layout/Layout.js